// src/pages/Projects.js
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteProject, getProjects, importProject } from '../services/ProjectServices';
import { faPlus, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import styles from './Table.module.css';
import '../components/MediaItems.css';

function Projects() {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projects, setProjects] = useState([]);
  const [section, setSection] = useState('details');
  const [showModal, setShowModal] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const lista = await getProjects();
        setProjects(lista);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [updateCounter]);

  const cancelDelete = () => {
    setDeleteInfo({ type: null, id: null, confirmationText: '' });
    setSection('details');
    setError(null);
  };

  const confirmDelete = (id) => {
    setProjectToDelete(id);
    setShowModal(true);
  };

  const handleDelete = async (id, force = false) => {
    setError(null);
    setLoading(true);
    try {
      await deleteProject(id, force);
      setUpdateCounter(updateCounter + 1);
      setSection('details');
    } catch (error) {
      setError(error.message);
      setDeleteInfo({ type: 'project', id, confirmationText: '' });
      setSection('deleteProject');
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      await handleDelete(projectToDelete);
      setShowModal(false);
      setProjectToDelete(null);
    }
  }

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      setLoading(true);

      // Convertir FileReader a Promise
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);

        reader.readAsText(file);
      });

      // Ahora el error de importProject SÍ se captura por este try/catch
      const res = await importProject(content);
      console.log("Import result:", res);

    } catch (err) {
      console.error('Import error', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };


  if (loading) {
    return <div>Cargando...</div>;
  }

  if (section === 'deleteProject') {
    return (
      <div className="delete-warning-container">
        <h2 style={{ color: 'red' }}>Advertencia</h2>
        <p>
          Si confirmas la eliminación del proyecto, se eliminarán también las etapas y orbit views relacionados.
          Esta operación no puede ser reversada.
        </p>
        <p>Por favor, escribe "eliminar proyecto" para confirmar:</p>
        <input
          type="text"
          placeholder="Escribe 'eliminar proyecto' aquí"
          onChange={(e) => setDeleteInfo({ ...deleteInfo, confirmationText: e.target.value })}
        />
        <div className="actions-container">
          <button
            className="btn-submit"
            disabled={deleteInfo.confirmationText !== "eliminar proyecto"}
            onClick={() => handleDelete(deleteInfo.id, true)}
          >
            Confirmar Eliminación
          </button>
          <button className="btn-cancel" onClick={cancelDelete}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }


  return (
    <div>
      <h2>Proyectos</h2>
      {error && (
        <div>
          <div className='error-message'>
            Hubo un error: {error}
          </div>
        </div>
      )}

      <div className="top-actions">
        <button className="btn-add" onClick={() => navigate('/projects/new')}>
          <FontAwesomeIcon icon={faPlus} />
        </button>

        <button className="btn-import" style={{ marginLeft: "10px" }} onClick={() => handleImportClick()}>
          Importar Proyecto
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />

      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id}>
              <td>{project.name}</td>
              <td>{project.description}</td>
              <td>
                <div className="actions-container">
                  <button className='btn-edit ' onClick={() => navigate(`/projects/edit/${project.id}`)}>
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                  <button className='btn-delete' onClick={() => confirmDelete(project.id)}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="media-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="media-modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-content-h3">¿Estás seguro de que deseas eliminar este proyecto?</h3>
            <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
              <button className="btn-submit" onClick={handleConfirmDelete}>Sí, eliminar</button>
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
